import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Paper, Typography, Box, Button, TextField, Tabs, Tab, Snackbar,
  FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip,
  Chip, FormControlLabel, Checkbox, Switch, Collapse, Divider
} from '@material-ui/core';
import {
  Add, Save as SaveIcon, Delete, Edit as EditIcon,
  Visibility, VisibilityOff, ExpandMore, ExpandLess,
  Streetview, Search, LocationSearching, Undo,
  CallMerge as MergeIcon, GroupWork as GroupIcon, LinkOff as UngroupIcon
} from '@material-ui/icons';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import { unionPolygons } from './polygonUnion';

import SiteTakeoffSummary from './SiteTakeoffSummary';
import LaborEstimate from './LaborEstimate';
import ProductionRateEditor from './ProductionRateEditor';
import {
  AREA_TYPES, AREA_TYPE_LABELS, AREA_TYPE_COLORS,
  PLANT_BEARING_TYPES, SHRUB_DENSITY_OPTIONS,
  MODIFIER_DEFS, DEFAULT_MODIFIERS, DEFAULT_PLANT_DENSITY,
  AREA_CATEGORIES, getAreaTypeLabel, getAreaTypeColor, resolveAreaType,
  SERVICES, SERVICE_LABELS, getSuggestedServices
} from './siteTakeoffConstants';
import {
  loadEsriLeaflet, checkYearService, enhancedGetPolygonArea,
  getGeometryFromLayer, defaultStyle,
  googleMapsProvider, googleSatelliteProvider, googleHybridProvider,
} from '../MapWithDrawing/Measure/mapUtils';

// ===== STYLES =====
const useStyles = makeStyles((theme) => ({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    padding: theme.spacing(1),
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  contentRow: {
    display: 'flex',
    flex: 1,
    gap: theme.spacing(1),
    overflow: 'hidden',
  },
  mapContainer: {
    flex: 1,
    position: 'relative',
    borderRadius: 4,
    overflow: 'hidden',
    border: '1px solid #ddd',
  },
  sidebar: {
    width: 480,
    minWidth: 480,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: 4,
    border: '1px solid #ddd',
    backgroundColor: '#fff',
  },
  sidebarScroll: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(1),
  },
  siteSelector: {
    padding: theme.spacing(1),
    borderBottom: '1px solid #eee',
  },
  paddedField: {
    padding: '4px',
    margin: '4px 0',
  },
  siteInfo: {
    padding: '8px',
    margin: '8px 0',
    backgroundColor: '#f8f8f8',
    borderRadius: 4,
  },
  areaListItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 8px',
    borderRadius: 4,
    border: '1px solid #e0e0e0',
    marginBottom: 4,
    cursor: 'pointer',
    transition: 'all 0.15s',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
  },
  areaListItemSelected: {
    border: '2px solid #1976d2',
    backgroundColor: 'rgba(25,118,210,0.04)',
  },
  colorDot: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    marginRight: 8,
    border: '1px solid rgba(0,0,0,0.15)',
    flexShrink: 0,
  },
  areaItemInfo: {
    flex: 1,
    minWidth: 0,
  },
  areaItemName: {
    fontSize: '0.82rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  areaItemMeta: {
    fontSize: '0.7rem',
    color: '#888',
  },
  editorSection: {
    padding: theme.spacing(1),
    backgroundColor: '#fafafa',
    borderTop: '2px solid #1976d2',
    marginTop: 8,
  },
  modifierRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '2px 0',
  },
  modifierLabel: {
    fontSize: '0.78rem',
    color: '#555',
  },
  drawingBanner: {
    position: 'absolute',
    top: 8,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1000,
    padding: '6px 16px',
    backgroundColor: '#1976d2',
    color: '#fff',
    borderRadius: 20,
    fontSize: '0.85rem',
    fontWeight: 500,
    pointerEvents: 'none',
  },
  serviceChip: {
    margin: 2,
    height: 26,
    fontSize: '0.72rem',
  },
  serviceChipActive: {
    margin: 2,
    height: 26,
    fontSize: '0.72rem',
    backgroundColor: '#e3f2fd',
    border: '1px solid #1976d2',
    fontWeight: 500,
  },
  serviceChipSuggested: {
    margin: 2,
    height: 26,
    fontSize: '0.72rem',
    backgroundColor: '#e8f5e9',
    border: '1px solid #4caf50',
  },
  servicesWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0,
    padding: '4px 0',
  },
  editorTabs: {
    minHeight: 36,
    backgroundColor: '#fff',
    borderBottom: '1px solid #ddd',
    marginBottom: 4,
    '& .MuiTab-root': { minHeight: 36, fontSize: '0.78rem', padding: '6px 10px', fontWeight: 500 },
    '& .Mui-selected': { color: '#1976d2', fontWeight: 600 },
  },
  saveBar: {
    padding: theme.spacing(1),
    borderTop: '1px solid #eee',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

// ===== COMPONENT =====
const SiteTakeoffPage = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const { jobsiteId } = useParams();
  const { url, user, clients } = props;

  // ----- Site / Client state -----
  const [selectedClient, setSelectedClient] = useState({});
  const [availableSites, setAvailableSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState({});
  const [jobSite, setJobSite] = useState(null);

  // ----- Map state -----
  const [providers, setProviders] = useState([]);
  const [currentProvider, setCurrentProvider] = useState(null);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const tileLayerRef = useRef(null);
  const drawnItemsRef = useRef(null);
  const drawControlRef = useRef(null);

  // ----- Areas state -----
  const [areas, setAreas] = useState([]);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const areasRef = useRef(areas);
  const layerMapRef = useRef({}); // areaId -> leaflet layer

  // ----- Rate overrides -----
  const [rateOverrides, setRateOverrides] = useState({});

  // ----- UI state -----
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' });
  const [saving, setSaving] = useState(false);
  const [modifiersExpanded, setModifiersExpanded] = useState(false);
  const [editorTab, setEditorTab] = useState(0);
  const [editingAreaId, setEditingAreaId] = useState(null);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelection, setMergeSelection] = useState([]); // array of area IDs selected for merge
  const [groupMode, setGroupMode] = useState(false);
  const [groupSelection, setGroupSelection] = useState([]); // area IDs selected for grouping
  const selectAreaRef = useRef(null); // ref to handleSelectArea for use in map click handlers

  useEffect(() => { areasRef.current = areas; }, [areas]);

  const selectedArea = useMemo(() => {
    return areas.find(a => (a._id || a.id) === selectedAreaId) || null;
  }, [areas, selectedAreaId]);

  // =============================================
  // MAP INITIALIZATION
  // =============================================
  useEffect(() => {
    if (!mapContainerRef.current || mapInstanceRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: [33.505, -112.15],
      zoom: 15,
      zoomControl: true,
    });

    // Default tile layer
    L.tileLayer(googleHybridProvider.url, {
      attribution: googleHybridProvider.attribution,
      maxZoom: 21,
    }).addTo(map);

    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);

    const drawControl = new L.Control.Draw({
      position: 'topleft',
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: { ...defaultStyle, fillOpacity: 0.3 },
        },
        rectangle: {
          shapeOptions: { ...defaultStyle, fillOpacity: 0.3 },
        },
        polyline: false,
        circle: false,
        circlemarker: false,
        marker: false,
      },
      edit: false,
    });
    map.addControl(drawControl);

    // Handle new polygon drawn
    map.on(L.Draw.Event.CREATED, (e) => {
      const layer = e.layer;
      drawnItems.addLayer(layer);

      const geometry = getGeometryFromLayer(layer);
      if (!geometry || !geometry.latlngs) return;

      const paths = geometry.latlngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
      const measurements = enhancedGetPolygonArea(paths);
      const newId = uuidv4();
      const defaultType = AREA_TYPES.GR_CLEAN;

      const newArea = {
        id: newId,
        name: '',
        areaType: defaultType,
        paths,
        fillColor: getAreaTypeColor(defaultType),
        strokeColor: '#000000',
        area: measurements.areaFeet.toFixed(2),
        areaAcres: measurements.areaAcres.toFixed(4),
        perimeter: measurements.perimeterFeet.toFixed(2),
        plantDensity: { ...DEFAULT_PLANT_DENSITY },
        modifiers: { ...DEFAULT_MODIFIERS },
        servicesRequired: getSuggestedServices(defaultType),
        notes: '',
        isNew: true,
      };

      // Style the layer
      layer.setStyle({
        fillColor: newArea.fillColor,
        fillOpacity: 0.3,
        color: '#000',
        weight: 2,
      });

      // Store layer reference
      layerMapRef.current[newId] = layer;

      // Add click handler to layer — use ref to get current handleSelectArea
      layer.on('click', () => {
        if (selectAreaRef.current) selectAreaRef.current(newId);
      });

      setAreas(prev => [...prev, newArea]);
      setSelectedAreaId(newId);
      setIsDrawing(false);
    });

    // (Per-polygon editing handled via handleStartEditShape / handleFinishEditShape)

    map.on('draw:drawstart', () => setIsDrawing(true));
    map.on('draw:drawstop', () => setIsDrawing(false));

    mapInstanceRef.current = map;
    drawnItemsRef.current = drawnItems;
    drawControlRef.current = drawControl;

    // Load Maricopa County aerial providers
    loadProviders();

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  // =============================================
  // AERIAL PROVIDERS
  // =============================================
  const loadProviders = async () => {
    try {
      await loadEsriLeaflet();
      const currentYear = new Date().getFullYear();
      const yearsToCheck = [];
      for (let y = currentYear; y >= 2017; y--) yearsToCheck.push(y);

      const results = await Promise.all(yearsToCheck.map(y => checkYearService(y)));
      const valid = results.filter(Boolean);
      const allProviders = [googleHybridProvider, googleSatelliteProvider, googleMapsProvider, ...valid];
      setProviders(allProviders);
    } catch (err) {
      console.error('Error loading providers:', err);
    }
  };

  const handleProviderChange = useCallback((provider) => {
    if (!mapInstanceRef.current) return;
    setCurrentProvider(provider);

    if (tileLayerRef.current) {
      mapInstanceRef.current.removeLayer(tileLayerRef.current);
    }

    if (provider.isExportService && window.L?.esri?.dynamicMapLayer) {
      tileLayerRef.current = window.L.esri.dynamicMapLayer({
        url: provider.url,
        maxZoom: provider.maxZoom || 21,
      }).addTo(mapInstanceRef.current);
    } else {
      tileLayerRef.current = L.tileLayer(provider.url, {
        attribution: provider.attribution,
        maxZoom: provider.maxZoom || 21,
      }).addTo(mapInstanceRef.current);
    }
  }, []);

  // =============================================
  // SITE LOADING
  // =============================================
  useEffect(() => {
    if (jobsiteId) {
      // Clear previous site data
      if (drawnItemsRef.current) drawnItemsRef.current.clearLayers();
      layerMapRef.current = {};
      setAreas([]);
      setSelectedAreaId(null);
      loadJobSite(jobsiteId);
    }
  }, [jobsiteId]);

  const loadJobSite = async (siteId) => {
    try {
      const resp = await axios.get(`${url}/api/customer/fetchJobSiteAreas?id=${siteId}&includeAll=1`);
      const { success, jobSite: siteData, jobSiteAreas } = resp.data;
      if (!success || !siteData?._id) return;

      setJobSite(siteData);
      setSelectedSite(siteData);

      // Restore saved production rate overrides
      if (siteData.productionRates && typeof siteData.productionRates === 'object') {
        setRateOverrides(siteData.productionRates);
      } else {
        setRateOverrides({});
      }

      if (siteData.client) {
        const client = typeof siteData.client === 'object' ? siteData.client : { _id: siteData.client };
        setSelectedClient(client);
        if (client._id) fetchClientSites(client._id);
      }

      // Center map on site — use GeoJSON coordinates first, fall back to lat/lng
      if (mapInstanceRef.current) {
        let centerCoords = null;
        if (siteData.location?.coordinates?.length === 2) {
          // GeoJSON is [lng, lat], Leaflet needs [lat, lng]
          centerCoords = [siteData.location.coordinates[1], siteData.location.coordinates[0]];
        } else if (siteData.latitude && siteData.longitude) {
          centerCoords = [siteData.latitude, siteData.longitude];
        }

        if (centerCoords) {
          const zoomLevel = (jobSiteAreas?.length > 0) ? 18 : 17;
          mapInstanceRef.current.setView(centerCoords, zoomLevel);
        }
      }

      // Load existing areas from the separate jobSiteAreas array
      const areasToLoad = jobSiteAreas?.length ? jobSiteAreas : [];
      if (areasToLoad.length) {
        const loaded = areasToLoad
          .filter(a => Array.isArray(a.paths) && a.paths.length > 0 && 'lat' in a.paths[0] && 'lng' in a.paths[0])
          .map(a => ({
            ...a,
            id: a.id || a._id,
            plantDensity: a.plantDensity || { ...DEFAULT_PLANT_DENSITY },
            modifiers: a.modifiers || { ...DEFAULT_MODIFIERS },
            servicesRequired: a.servicesRequired?.length ? a.servicesRequired : getSuggestedServices(a.areaType),
            notes: a.notes || '',
            isNew: false,
          }));
        setAreas(loaded);

        // Render existing areas on map
        loaded.forEach(area => {
          if (!area.paths?.length || !drawnItemsRef.current) return;
          const latlngs = area.paths.map(p => [p.lat, p.lng]);
          const color = getAreaTypeColor(area.areaType);
          const polygon = L.polygon(latlngs, {
            fillColor: color,
            fillOpacity: 0.3,
            color: area.strokeColor || '#000',
            weight: 2,
          });
          drawnItemsRef.current.addLayer(polygon);
          layerMapRef.current[area._id || area.id] = polygon;
          polygon.on('click', () => {
            if (selectAreaRef.current) selectAreaRef.current(area._id || area.id);
          });
        });

        showSnackbar(`Loaded ${loaded.length} areas for ${siteData.name || ''}`, 'success');
      }
    } catch (err) {
      console.error('Error loading jobsite:', err);
      showSnackbar('Error loading jobsite', 'error');
    }
  };

  // =============================================
  // CLIENT / SITE SELECTION
  // =============================================
  const fetchClientSites = async (clientId) => {
    try {
      const resp = await axios.get(`${url}/api/customer/getAllJobsites?id=${clientId}&sitesOnly=true`);
      setAvailableSites(resp.data || []);
    } catch (err) {
      console.error('Error fetching sites:', err);
    }
  };

  const handleClientChange = useCallback((e, newClient) => {
    setSelectedClient(newClient || {});
    setSelectedSite({});
    setAvailableSites([]);
    if (newClient?._id) fetchClientSites(newClient._id);
  }, [url]);

  const handleSiteChange = useCallback((e, newSite) => {
    if (!newSite?._id) return;
    // Clear existing areas/layers before loading new site
    if (drawnItemsRef.current) drawnItemsRef.current.clearLayers();
    layerMapRef.current = {};
    setAreas([]);
    setSelectedAreaId(null);
    setSelectedSite(newSite);
    // Navigate to the site — useEffect on jobsiteId will trigger loadJobSite
    history.push(`/site-takeoff/${newSite._id}`);
  }, [history]);

  // =============================================
  // AREA EDITING
  // =============================================
  const handleUpdateArea = useCallback((areaId, field, value) => {
    setAreas(prev => prev.map(a => {
      if ((a._id || a.id) !== areaId) return a;

      const updated = { ...a };

      // Handle nested fields
      if (field === 'plantDensity') {
        updated.plantDensity = { ...(a.plantDensity || DEFAULT_PLANT_DENSITY), ...value };
      } else if (field === 'modifiers') {
        updated.modifiers = { ...(a.modifiers || DEFAULT_MODIFIERS), ...value };
      } else if (field === 'servicesRequired') {
        updated.servicesRequired = value;
      } else if (field === 'areaType') {
        updated.areaType = value;
        // Update color on map
        const color = getAreaTypeColor(value);
        updated.fillColor = color;
        const layer = layerMapRef.current[areaId];
        if (layer) layer.setStyle({ fillColor: color, fillOpacity: 0.3 });
        // Auto-suggest services for new type (only if user hasn't manually edited)
        updated.servicesRequired = getSuggestedServices(value);
      } else {
        updated[field] = value;
      }

      return updated;
    }));
  }, []);

  // Helper: disable editing on a layer and restore its style
  const finishEditOnLayer = useCallback((id) => {
    const layer = layerMapRef.current[id];
    if (!layer || !layer.editing) return;
    layer.editing.disable();

    // Recalculate geometry
    const geometry = getGeometryFromLayer(layer);
    if (geometry && geometry.latlngs) {
      const paths = geometry.latlngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
      const measurements = enhancedGetPolygonArea(paths);

      setAreas(prev => prev.map(a => {
        if ((a._id || a.id) !== id) return a;
        return {
          ...a,
          paths,
          area: measurements.areaFeet.toFixed(2),
          areaAcres: measurements.areaAcres.toFixed(4),
          perimeter: measurements.perimeterFeet.toFixed(2),
        };
      }));
    }

    // Restore normal style
    const area = areasRef.current.find(a => (a._id || a.id) === id);
    layer.setStyle({
      weight: 2, dashArray: null, color: '#000',
      fillColor: area ? area.fillColor : '#3388ff', fillOpacity: 0.3,
    });
  }, []);

  // Finish editing and recalculate measurements
  const handleFinishEditShape = useCallback((areaId) => {
    const id = areaId || editingAreaId;
    if (!id) return;
    finishEditOnLayer(id);
    setEditingAreaId(null);
  }, [editingAreaId, finishEditOnLayer]);

  // Start editing a single polygon's shape
  const handleStartEditShape = useCallback((areaId) => {
    // Finish any existing edit first
    if (editingAreaId) {
      finishEditOnLayer(editingAreaId);
    }
    const layer = layerMapRef.current[areaId];
    if (layer && layer.editing) {
      layer.editing.enable();
      layer.setStyle({ weight: 3, dashArray: '5, 8', color: '#1976d2' });
      setEditingAreaId(areaId);
      setSelectedAreaId(areaId);
    }
  }, [editingAreaId, finishEditOnLayer]);

  // Cancel edit without saving changes — revert polygon to stored paths
  const handleCancelEditShape = useCallback(() => {
    if (!editingAreaId) return;
    const layer = layerMapRef.current[editingAreaId];
    const area = areasRef.current.find(a => (a._id || a.id) === editingAreaId);
    if (layer && layer.editing) {
      layer.editing.disable();
      // Revert to saved paths
      if (area && area.paths && area.paths.length > 0) {
        const latlngs = area.paths.map(p => L.latLng(p.lat, p.lng));
        layer.setLatLngs(latlngs);
      }
      layer.setStyle({
        weight: 2, dashArray: null, color: '#000',
        fillColor: area ? area.fillColor : '#3388ff', fillOpacity: 0.3,
      });
    }
    setEditingAreaId(null);
  }, [editingAreaId]);

  const handleSelectArea = useCallback((areaId) => {
    // Finish any in-progress shape edit before switching
    if (editingAreaId && editingAreaId !== areaId) {
      finishEditOnLayer(editingAreaId);
      setEditingAreaId(null);
    }

    // Un-highlight previously selected polygon — restore original fill
    if (selectedAreaId && selectedAreaId !== areaId) {
      const prevLayer = layerMapRef.current[selectedAreaId];
      if (prevLayer && (!editingAreaId || editingAreaId !== selectedAreaId)) {
        const prevArea = areasRef.current.find(a => (a._id || a.id) === selectedAreaId);
        prevLayer.setStyle({
          weight: 2, color: '#000', opacity: 0.8,
          fillColor: prevArea ? prevArea.fillColor : '#3388ff',
          fillOpacity: 0.3,
        });
      }
    }

    // Highlight newly selected polygon — bright border + boosted fill
    const layer = layerMapRef.current[areaId];
    if (layer) {
      layer.setStyle({
        weight: 4, color: '#ffeb3b', opacity: 1,
        fillColor: '#ffeb3b', fillOpacity: 0.45,
      });
      layer.bringToFront();
      if (mapInstanceRef.current) {
        mapInstanceRef.current.fitBounds(layer.getBounds(), { padding: [50, 50], maxZoom: 19 });
      }
    }

    setSelectedAreaId(areaId);
    setEditorTab(0);
  }, [editingAreaId, finishEditOnLayer, selectedAreaId]);

  // Keep ref in sync so map click handlers can use it
  useEffect(() => { selectAreaRef.current = handleSelectArea; }, [handleSelectArea]);

  const handleDeleteArea = useCallback((areaId) => {
    // Cancel edit if deleting the area being edited
    if (editingAreaId === areaId) setEditingAreaId(null);
    const layer = layerMapRef.current[areaId];
    if (layer) {
      if (layer.editing) layer.editing.disable();
      if (drawnItemsRef.current) drawnItemsRef.current.removeLayer(layer);
    }
    delete layerMapRef.current[areaId];
    setAreas(prev => prev.filter(a => (a._id || a.id) !== areaId));
    if (selectedAreaId === areaId) setSelectedAreaId(null);
  }, [selectedAreaId, editingAreaId]);

  // =============================================
  // MERGE AREAS
  // =============================================
  const handleToggleMergeSelect = useCallback((areaId) => {
    setMergeSelection(prev => {
      const isRemoving = prev.includes(areaId);
      const layer = layerMapRef.current[areaId];
      if (layer) {
        if (isRemoving) {
          // Restore normal style
          const area = areasRef.current.find(a => (a._id || a.id) === areaId);
          layer.setStyle({ weight: 2, color: '#000', dashArray: null, opacity: 0.8 });
        } else {
          // Highlight for merge
          layer.setStyle({ weight: 4, color: '#ff9800', dashArray: '6, 4', opacity: 1 });
          layer.bringToFront();
        }
      }
      if (isRemoving) return prev.filter(id => id !== areaId);
      return [...prev, areaId];
    });
  }, []);

  const handleMergeAreas = useCallback(() => {
    if (mergeSelection.length < 2) {
      showSnackbar('Select at least 2 areas to merge', 'error');
      return;
    }

    const selectedAreas = mergeSelection
      .map(id => areasRef.current.find(a => (a._id || a.id) === id))
      .filter(Boolean);

    if (selectedAreas.length < 2) return;

    try {
      // Union all selected polygons
      const polyPaths = selectedAreas.map(a => a.paths);
      const paths = unionPolygons(polyPaths);

      if (!paths || paths.length < 3) {
        showSnackbar('Could not merge — try selecting adjacent areas', 'error');
        return;
      }

      const measurements = enhancedGetPolygonArea(paths);
      const primary = selectedAreas[0]; // first selected area is the "primary"
      const newId = uuidv4();

      const mergedArea = {
        id: newId,
        name: primary.name || getAreaTypeLabel(primary.areaType),
        areaType: primary.areaType,
        paths,
        fillColor: primary.fillColor,
        strokeColor: primary.strokeColor || '#000000',
        area: measurements.areaFeet.toFixed(2),
        areaAcres: measurements.areaAcres.toFixed(4),
        perimeter: measurements.perimeterFeet.toFixed(2),
        plantDensity: { ...DEFAULT_PLANT_DENSITY, ...(primary.plantDensity || {}) },
        modifiers: { ...DEFAULT_MODIFIERS, ...(primary.modifiers || {}) },
        servicesRequired: [...(primary.servicesRequired || [])],
        notes: selectedAreas.map(a => a.notes).filter(Boolean).join('; '),
        isNew: true,
      };

      // Sum tree counts from all merged areas
      mergedArea.plantDensity.treeCount = selectedAreas.reduce(
        (sum, a) => sum + (a.plantDensity?.treeCount || 0), 0
      );

      // Remove old layers from map
      mergeSelection.forEach(id => {
        const layer = layerMapRef.current[id];
        if (layer) {
          if (layer.editing) layer.editing.disable();
          if (drawnItemsRef.current) drawnItemsRef.current.removeLayer(layer);
        }
        delete layerMapRef.current[id];
      });

      // Add new merged polygon to map
      const latlngs = paths.map(p => L.latLng(p.lat, p.lng));
      const newLayer = L.polygon(latlngs, {
        fillColor: mergedArea.fillColor,
        fillOpacity: 0.3,
        color: '#000',
        weight: 2,
      });
      drawnItemsRef.current.addLayer(newLayer);
      layerMapRef.current[newId] = newLayer;
      newLayer.on('click', () => {
        if (selectAreaRef.current) selectAreaRef.current(newId);
      });

      // Update state: remove old areas, add merged
      setAreas(prev => {
        const filtered = prev.filter(a => !mergeSelection.includes(a._id || a.id));
        return [...filtered, mergedArea];
      });

      setSelectedAreaId(newId);
      setMergeSelection([]);
      setMergeMode(false);
      showSnackbar(`Merged ${selectedAreas.length} areas into "${mergedArea.name}"`, 'success');
    } catch (err) {
      console.error('Merge error:', err);
      showSnackbar('Merge failed: ' + (err.message || 'Unknown error'), 'error');
    }
  }, [mergeSelection]);

  // =============================================
  // GROUPING (logical — no geometry merge)
  // =============================================
  const handleToggleGroupSelect = useCallback((areaId) => {
    setGroupSelection(prev => {
      const isRemoving = prev.includes(areaId);
      const layer = layerMapRef.current[areaId];
      if (layer) {
        if (isRemoving) {
          const area = areasRef.current.find(a => (a._id || a.id) === areaId);
          layer.setStyle({ weight: 2, color: '#000', dashArray: null, opacity: 0.8,
            fillColor: area ? area.fillColor : '#3388ff', fillOpacity: 0.3 });
        } else {
          layer.setStyle({ weight: 4, color: '#7b1fa2', dashArray: '6, 4', opacity: 1,
            fillColor: '#ce93d8', fillOpacity: 0.4 });
          layer.bringToFront();
        }
      }
      if (isRemoving) return prev.filter(id => id !== areaId);
      return [...prev, areaId];
    });
  }, []);

  const handleCreateGroup = useCallback(() => {
    if (groupSelection.length < 2) {
      showSnackbar('Select at least 2 areas to group', 'error');
      return;
    }

    // Build a group name from the area types
    const selectedAreas = groupSelection
      .map(id => areasRef.current.find(a => (a._id || a.id) === id))
      .filter(Boolean);

    const primaryType = selectedAreas[0]?.areaType;
    const defaultName = getAreaTypeLabel(primaryType) + ' Group';

    const name = window.prompt('Group name:', defaultName);
    if (!name) return; // cancelled

    const gid = uuidv4();

    // Restore polygon styles before updating state
    groupSelection.forEach(id => {
      const layer = layerMapRef.current[id];
      const area = areasRef.current.find(a => (a._id || a.id) === id);
      if (layer) {
        layer.setStyle({ weight: 2, color: '#000', dashArray: null, opacity: 0.8,
          fillColor: area ? area.fillColor : '#3388ff', fillOpacity: 0.3 });
      }
    });

    setAreas(prev => prev.map(a => {
      if (!groupSelection.includes(a._id || a.id)) return a;
      return { ...a, groupId: gid, groupName: name };
    }));

    setGroupSelection([]);
    setGroupMode(false);
    showSnackbar(`Grouped ${selectedAreas.length} areas as "${name}"`, 'success');
  }, [groupSelection]);

  const handleUngroupArea = useCallback((areaId) => {
    setAreas(prev => prev.map(a => {
      if ((a._id || a.id) !== areaId) return a;
      const { groupId, groupName, ...rest } = a;
      return rest;
    }));
  }, []);

  const handleUngroupAll = useCallback((groupId) => {
    setAreas(prev => prev.map(a => {
      if (a.groupId !== groupId) return a;
      const { groupId: _gid, groupName: _gn, ...rest } = a;
      return rest;
    }));
    showSnackbar('Group removed', 'success');
  }, []);

  // =============================================
  // SAVE
  // =============================================
  const handleSave = async () => {
    if (!jobSite?._id) {
      showSnackbar('No jobsite selected', 'error');
      return;
    }

    setSaving(true);
    try {
      const areasToSave = areas.map(a => ({
        _id: a._id || undefined,
        id: a.id,
        name: a.name || getAreaTypeLabel(a.areaType),
        areaType: a.areaType,
        paths: a.paths,
        fillColor: a.fillColor,
        strokeColor: a.strokeColor || '#000000',
        area: a.area,
        areaAcres: a.areaAcres,
        perimeter: a.perimeter,
        plantDensity: a.plantDensity || DEFAULT_PLANT_DENSITY,
        modifiers: a.modifiers || DEFAULT_MODIFIERS,
        servicesRequired: a.servicesRequired || [],
        notes: a.notes || '',
        groupId: a.groupId || undefined,
        groupName: a.groupName || undefined,
      }));

      const totalArea = areas.reduce((s, a) => s + (parseFloat(a.area) || 0), 0);
      const totalPerimeter = areas.reduce((s, a) => s + (parseFloat(a.perimeter) || 0), 0);

      await axios.post(`${url}/api/hmi/genericPostRequest?type=SAVE_HERBICIDE_AREAS`, {
        jobSite,
        areas: areasToSave,
        totalArea: totalArea.toFixed(2),
        netArea: totalArea.toFixed(2),
        totalPerimeter: totalPerimeter.toFixed(2),
        productionRates: Object.keys(rateOverrides).length > 0 ? rateOverrides : undefined,
      });

      // Mark all as saved
      setAreas(prev => prev.map(a => ({ ...a, isNew: false })));
      showSnackbar('Areas saved successfully', 'success');
    } catch (err) {
      console.error('Error saving areas:', err);
      showSnackbar('Error saving areas', 'error');
    } finally {
      setSaving(false);
    }
  };

  // =============================================
  // HELPERS
  // =============================================
  const showSnackbar = (message, type = 'success') => {
    setSnackbar({ open: true, message, type });
  };

  const handleOpenGoogleStreetView = () => {
    if (!selectedSite?.latitude || !selectedSite?.longitude) return;
    const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${selectedSite.latitude},${selectedSite.longitude}`;
    window.open(url, '_blank');
  };

  const handleOpenMCMaps = () => {
    if (!selectedSite?.address) return;
    const addy = `${selectedSite.address}, ${selectedSite.zip || ''}`;
    const url = `https://maps.mcassessor.maricopa.gov/?search=${encodeURIComponent(addy)}`;
    window.open(url, '_blank');
  };

  // =============================================
  // RENDER
  // =============================================
  return (
    <div className={classes.pageContainer}>
      {/* Header */}
      <div className={classes.header}>
        <Typography variant="h6">Site Takeoff</Typography>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {providers.length > 1 && (
            <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
              <InputLabel>Map Layer</InputLabel>
              <Select
                label="Map Layer"
                value={currentProvider?.name || ''}
                onChange={(e) => {
                  const p = providers.find(p => p.name === e.target.value);
                  if (p) handleProviderChange(p);
                }}
              >
                {providers.map(p => (
                  <MenuItem key={p.name} value={p.name}>{p.displayName || p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Chip label={`${areas.length} Areas`} size="small" style={{ backgroundColor: '#accacd' }} />
        </div>
      </div>

      {/* Main Content */}
      <div className={classes.contentRow}>
        {/* Map */}
        <div className={classes.mapContainer}>
          <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
          {isDrawing && (
            <div className={classes.drawingBanner}>
              Click to place points, double-click to finish
            </div>
          )}
          {editingAreaId && (
            <div className={classes.drawingBanner} style={{ backgroundColor: '#1565c0' }}>
              Editing shape — drag vertices to adjust
            </div>
          )}
        </div>

        {/* Sidebar */}
        <Paper className={classes.sidebar} elevation={1}>
          {/* Site Selector */}
          <div className={classes.siteSelector}>
            <Autocomplete
              id="st-customer"
              options={clients || []}
              getOptionLabel={(opt) => opt?.displayName || opt?.searchName || ''}
              className={classes.paddedField}
              onChange={handleClientChange}
              fullWidth
              size="small"
              value={selectedClient}
              renderInput={(params) => <TextField variant="outlined" {...params} label="Customer" />}
            />
            <Autocomplete
              id="st-jobsite"
              options={availableSites || []}
              getOptionLabel={(site) => `${site?.name || ''} ${site?.address || ''}`}
              className={classes.paddedField}
              onChange={handleSiteChange}
              fullWidth
              size="small"
              value={selectedSite}
              renderInput={(params) => <TextField variant="outlined" {...params} label="Jobsite" />}
            />
            {selectedSite?._id && (
              <div className={classes.siteInfo}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" noWrap style={{ maxWidth: '70%' }}>
                    {selectedSite.name} {selectedSite.address}
                  </Typography>
                  <div>
                    <Tooltip title="MC Assessor Maps">
                      <IconButton size="small" onClick={handleOpenMCMaps}><Search /></IconButton>
                    </Tooltip>
                    <Tooltip title="Google Street View">
                      <IconButton size="small" onClick={handleOpenGoogleStreetView}><Streetview /></IconButton>
                    </Tooltip>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Areas" />
            <Tab label="Takeoff" />
          </Tabs>

          {/* Tab Content */}
          <div className={classes.sidebarScroll}>
            {activeTab === 0 && (
              <>
                {/* Merge / Group toolbar */}
                {areas.length >= 2 && (
                  <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" px={1} py={0.5}
                    style={{
                      borderBottom: (mergeMode || groupMode) ? `2px solid ${mergeMode ? '#ff9800' : '#7b1fa2'}` : 'none',
                      backgroundColor: mergeMode ? '#fff3e0' : groupMode ? '#f3e5f5' : 'transparent',
                    }}
                  >
                    <Box display="flex" gap={0.5} style={{ gap: 4 }}>
                      <Button
                        size="small"
                        variant={mergeMode ? 'contained' : 'outlined'}
                        startIcon={<MergeIcon />}
                        disabled={groupMode}
                        style={{ fontSize: '0.72rem', textTransform: 'none', backgroundColor: mergeMode ? '#ff9800' : undefined, color: mergeMode ? '#fff' : undefined }}
                        onClick={() => {
                          if (mergeMode) { setMergeMode(false); setMergeSelection([]); }
                          else { setMergeMode(true); setMergeSelection([]); }
                        }}
                      >
                        {mergeMode ? 'Cancel' : 'Merge'}
                      </Button>
                      <Button
                        size="small"
                        variant={groupMode ? 'contained' : 'outlined'}
                        startIcon={<GroupIcon />}
                        disabled={mergeMode}
                        style={{ fontSize: '0.72rem', textTransform: 'none', backgroundColor: groupMode ? '#7b1fa2' : undefined, color: groupMode ? '#fff' : undefined }}
                        onClick={() => {
                          if (groupMode) { setGroupMode(false); setGroupSelection([]); }
                          else { setGroupMode(true); setGroupSelection([]); }
                        }}
                      >
                        {groupMode ? 'Cancel' : 'Group'}
                      </Button>
                    </Box>
                    {mergeMode && mergeSelection.length >= 2 && (
                      <Button size="small" variant="contained" color="primary"
                        style={{ fontSize: '0.72rem', textTransform: 'none' }} onClick={handleMergeAreas}
                      >Merge {mergeSelection.length}</Button>
                    )}
                    {mergeMode && mergeSelection.length < 2 && (
                      <Typography variant="caption" color="textSecondary">Select {2 - mergeSelection.length} more</Typography>
                    )}
                    {groupMode && groupSelection.length >= 2 && (
                      <Button size="small" variant="contained"
                        style={{ fontSize: '0.72rem', textTransform: 'none', backgroundColor: '#7b1fa2', color: '#fff' }}
                        onClick={handleCreateGroup}
                      >Group {groupSelection.length}</Button>
                    )}
                    {groupMode && groupSelection.length < 2 && (
                      <Typography variant="caption" color="textSecondary">Select {2 - groupSelection.length} more</Typography>
                    )}
                  </Box>
                )}

                {/* Area List — organized by groups */}
                {(() => {
                  const selectionMode = mergeMode || groupMode;
                  // Separate grouped vs ungrouped
                  const groups = {};
                  const ungrouped = [];
                  areas.forEach(area => {
                    if (area.groupId) {
                      if (!groups[area.groupId]) groups[area.groupId] = { name: area.groupName || 'Unnamed Group', areas: [] };
                      groups[area.groupId].areas.push(area);
                    } else {
                      ungrouped.push(area);
                    }
                  });

                  const renderAreaItem = (area) => {
                    const aId = area._id || area.id;
                    const isSelected = aId === selectedAreaId;
                    const isMergeSelected = mergeSelection.includes(aId);
                    const isGroupSelected = groupSelection.includes(aId);
                    const isChecked = isMergeSelected || isGroupSelected;
                    const color = getAreaTypeColor(area.areaType);
                    const highlightStyle = isMergeSelected
                      ? { border: '2px solid #ff9800', backgroundColor: 'rgba(255,152,0,0.06)' }
                      : isGroupSelected
                        ? { border: '2px solid #7b1fa2', backgroundColor: 'rgba(123,31,162,0.06)' }
                        : undefined;

                    return (
                      <div
                        key={aId}
                        className={`${classes.areaListItem} ${isSelected && !selectionMode ? classes.areaListItemSelected : ''}`}
                        style={highlightStyle}
                        onClick={() => {
                          if (mergeMode) handleToggleMergeSelect(aId);
                          else if (groupMode) handleToggleGroupSelect(aId);
                          else handleSelectArea(aId);
                        }}
                      >
                        {selectionMode && (
                          <Checkbox
                            size="small"
                            checked={isChecked}
                            style={{ padding: 2, marginRight: 4 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (mergeMode) handleToggleMergeSelect(aId);
                              else handleToggleGroupSelect(aId);
                            }}
                          />
                        )}
                        <div className={classes.colorDot} style={{ backgroundColor: color }} />
                        <div className={classes.areaItemInfo}>
                          <div className={classes.areaItemName}>
                            {area.name || getAreaTypeLabel(area.areaType)}
                            {area.isNew && (
                              <Chip size="small" label="new" style={{ height: 16, fontSize: '0.6rem', marginLeft: 4, backgroundColor: '#ff9800', color: '#fff' }} />
                            )}
                          </div>
                          <div className={classes.areaItemMeta}>
                            {getAreaTypeLabel(area.areaType)} | {parseFloat(area.area || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} SF
                          </div>
                        </div>
                        {!selectionMode && (
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteArea(aId); }}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                    );
                  };

                  return (
                    <>
                      {/* Grouped areas */}
                      {Object.entries(groups).map(([gid, group]) => {
                        const groupSF = group.areas.reduce((s, a) => s + (parseFloat(a.area) || 0), 0);
                        return (
                          <Box key={gid} mb={0.5}>
                            <Box display="flex" alignItems="center" px={1} py={0.5}
                              style={{ backgroundColor: '#f3e5f5', borderRadius: 4, borderLeft: '3px solid #7b1fa2' }}
                            >
                              <GroupIcon style={{ fontSize: 16, color: '#7b1fa2', marginRight: 6 }} />
                              <Typography style={{ fontSize: '0.78rem', fontWeight: 600, flex: 1, color: '#4a148c' }}>
                                {group.name}
                              </Typography>
                              <Typography style={{ fontSize: '0.7rem', color: '#7b1fa2', marginRight: 4 }}>
                                {group.areas.length} areas | {groupSF.toLocaleString('en-US', { maximumFractionDigits: 0 })} SF
                              </Typography>
                              {!selectionMode && (
                                <Tooltip title="Ungroup all">
                                  <IconButton size="small" onClick={() => handleUngroupAll(gid)}>
                                    <UngroupIcon style={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <Box pl={1.5}>
                              {group.areas.map(renderAreaItem)}
                            </Box>
                          </Box>
                        );
                      })}

                      {/* Ungrouped areas */}
                      {ungrouped.map(renderAreaItem)}
                    </>
                  );
                })()}

                {areas.length === 0 && (
                  <Typography variant="body2" color="textSecondary" style={{ padding: 16, textAlign: 'center' }}>
                    Use the drawing tools on the map to create areas.
                  </Typography>
                )}

                {/* Editor for selected area */}
                {selectedArea && (() => {
                  const svcCount = (selectedArea.servicesRequired || []).length;
                  const mods = selectedArea.modifiers || {};
                  const modCount = [
                    mods.access === 'obstructed', mods.slope === 'sloped',
                    mods.litterExposure === 'high', mods.weedPressure !== 'low' && mods.weedPressure,
                    mods.limitedAccess, mods.blowRestrictions,
                    mods.irrigationIncluded !== 'none' && mods.irrigationIncluded,
                  ].filter(Boolean).length;

                  return (
                  <div className={classes.editorSection}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Typography variant="subtitle2">
                        Edit: {selectedArea.name || getAreaTypeLabel(selectedArea.areaType)}
                      </Typography>
                      {editingAreaId === (selectedArea._id || selectedArea.id) ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            style={{ fontSize: '0.7rem', textTransform: 'none', minWidth: 0, padding: '2px 10px' }}
                            onClick={() => handleFinishEditShape(selectedArea._id || selectedArea.id)}
                          >
                            Done
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            style={{ fontSize: '0.7rem', textTransform: 'none', minWidth: 0, padding: '2px 10px' }}
                            onClick={handleCancelEditShape}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Tooltip title="Edit polygon shape on map">
                          <IconButton
                            size="small"
                            onClick={() => handleStartEditShape(selectedArea._id || selectedArea.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>

                    {editingAreaId === (selectedArea._id || selectedArea.id) && (
                      <Box p={1} mb={1} bgcolor="#e3f2fd" borderRadius={4} style={{ fontSize: '0.78rem', color: '#1565c0' }}>
                        Drag vertices to reshape this area. Click Done when finished.
                      </Box>
                    )}

                    {/* Editor Tabs */}
                    <Tabs
                      value={editorTab}
                      onChange={(e, v) => setEditorTab(v)}
                      className={classes.editorTabs}
                      indicatorColor="primary"
                      textColor="primary"
                      variant="fullWidth"
                    >
                      <Tab label="Details" />
                      <Tab label={svcCount ? `Services (${svcCount})` : 'Services'} />
                    </Tabs>

                    {/* ===== DETAILS TAB ===== */}
                    {editorTab === 0 && (
                      <Box pt={1}>
                        <TextField
                          label="Area Name"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={selectedArea.name || ''}
                          onChange={(e) => handleUpdateArea(selectedAreaId, 'name', e.target.value)}
                          style={{ marginBottom: 8 }}
                        />

                        {/* Group badge */}
                        {selectedArea.groupId && (
                          <Box display="flex" alignItems="center" mb={1} px={1} py={0.5}
                            style={{ backgroundColor: '#f3e5f5', borderRadius: 4, borderLeft: '3px solid #7b1fa2' }}
                          >
                            <GroupIcon style={{ fontSize: 14, color: '#7b1fa2', marginRight: 6 }} />
                            <Typography style={{ fontSize: '0.75rem', flex: 1, color: '#4a148c' }}>
                              {selectedArea.groupName}
                            </Typography>
                            <Tooltip title="Remove from group">
                              <IconButton size="small" onClick={() => handleUngroupArea(selectedAreaId)}>
                                <UngroupIcon style={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}

                        <FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 8 }}>
                          <InputLabel>Area Type</InputLabel>
                          <Select
                            label="Area Type"
                            value={selectedArea.areaType || AREA_TYPES.GR_CLEAN}
                            onChange={(e) => handleUpdateArea(selectedAreaId, 'areaType', e.target.value)}
                          >
                            {Object.entries(AREA_CATEGORIES).map(([catKey, cat]) => [
                              <MenuItem key={`header-${catKey}`} disabled style={{ opacity: 1, fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#f5f5f5' }}>
                                {cat.label}
                              </MenuItem>,
                              ...cat.types.map(t => (
                                <MenuItem key={t} value={t}>
                                  <span style={{
                                    width: 10, height: 10, borderRadius: '50%',
                                    backgroundColor: AREA_TYPE_COLORS[t],
                                    display: 'inline-block', marginRight: 8,
                                    border: '1px solid rgba(0,0,0,0.15)',
                                  }} />
                                  {AREA_TYPE_LABELS[t]}
                                </MenuItem>
                              ))
                            ])}
                          </Select>
                        </FormControl>

                        {/* Geometry info */}
                        <Box p={1} mb={1} bgcolor="#f0f0f0" borderRadius={4} style={{ fontSize: '0.78rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Area:</span>
                            <strong>{parseFloat(selectedArea.area || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} SF</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Perimeter:</span>
                            <strong>{parseFloat(selectedArea.perimeter || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} LF</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Acres:</span>
                            <strong>{parseFloat(selectedArea.areaAcres || 0).toFixed(3)}</strong>
                          </div>
                        </Box>

                        {/* Plant Density — only for plant-bearing types */}
                        {PLANT_BEARING_TYPES.includes(resolveAreaType(selectedArea.areaType)) && (
                          <Box mb={1}>
                            <Typography variant="caption" style={{ fontWeight: 600 }}>Plant Density</Typography>
                            <TextField
                              label="Tree Count"
                              variant="outlined"
                              size="small"
                              type="number"
                              fullWidth
                              value={selectedArea.plantDensity?.treeCount || 0}
                              onChange={(e) => handleUpdateArea(selectedAreaId, 'plantDensity', {
                                treeCount: Math.max(0, parseInt(e.target.value) || 0)
                              })}
                              inputProps={{ min: 0 }}
                              style={{ marginBottom: 4, marginTop: 4 }}
                            />
                            <FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 4 }}>
                              <InputLabel>Shrub Density</InputLabel>
                              <Select
                                label="Shrub Density"
                                value={selectedArea.plantDensity?.shrubDensity || 'none'}
                                onChange={(e) => handleUpdateArea(selectedAreaId, 'plantDensity', {
                                  shrubDensity: e.target.value
                                })}
                              >
                                {SHRUB_DENSITY_OPTIONS.map(o => (
                                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <FormControl variant="outlined" size="small" fullWidth>
                              <InputLabel>Groundcover Density</InputLabel>
                              <Select
                                label="Groundcover Density"
                                value={selectedArea.plantDensity?.groundcoverDensity || 'none'}
                                onChange={(e) => handleUpdateArea(selectedAreaId, 'plantDensity', {
                                  groundcoverDensity: e.target.value
                                })}
                              >
                                {SHRUB_DENSITY_OPTIONS.map(o => (
                                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        )}

                        {/* Notes */}
                        <TextField
                          label="Notes"
                          variant="outlined"
                          size="small"
                          fullWidth
                          multiline
                          rows={2}
                          value={selectedArea.notes || ''}
                          onChange={(e) => handleUpdateArea(selectedAreaId, 'notes', e.target.value)}
                          style={{ marginBottom: 8 }}
                        />

                        {/* Modifiers — collapsible */}
                        <Box mb={1}>
                          <div
                            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '4px 0' }}
                            onClick={() => setModifiersExpanded(!modifiersExpanded)}
                          >
                            <Typography variant="caption" style={{ fontWeight: 600, flex: 1 }}>
                              Modifiers{modCount > 0 ? ` (${modCount} active)` : ''}
                            </Typography>
                            {modifiersExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                          </div>

                          <Collapse in={modifiersExpanded}>
                            <Box pl={1}>
                              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 4 }}>
                                Site conditions that affect production rates.
                              </Typography>
                              {/* Access */}
                              <div className={classes.modifierRow}>
                                <span className={classes.modifierLabel}>Access</span>
                                <Select
                                  size="small"
                                  value={selectedArea.modifiers?.access || 'open'}
                                  onChange={(e) => handleUpdateArea(selectedAreaId, 'modifiers', { access: e.target.value })}
                                  style={{ fontSize: '0.78rem' }}
                                >
                                  <MenuItem value="open">Open</MenuItem>
                                  <MenuItem value="obstructed">Obstructed</MenuItem>
                                </Select>
                              </div>
                              {/* Slope */}
                              <div className={classes.modifierRow}>
                                <span className={classes.modifierLabel}>Slope</span>
                                <Select
                                  size="small"
                                  value={selectedArea.modifiers?.slope || 'flat'}
                                  onChange={(e) => handleUpdateArea(selectedAreaId, 'modifiers', { slope: e.target.value })}
                                  style={{ fontSize: '0.78rem' }}
                                >
                                  <MenuItem value="flat">Flat</MenuItem>
                                  <MenuItem value="sloped">Sloped</MenuItem>
                                </Select>
                              </div>
                              {/* Litter Exposure */}
                              <div className={classes.modifierRow}>
                                <span className={classes.modifierLabel}>Litter Exposure</span>
                                <Select
                                  size="small"
                                  value={selectedArea.modifiers?.litterExposure || 'low'}
                                  onChange={(e) => handleUpdateArea(selectedAreaId, 'modifiers', { litterExposure: e.target.value })}
                                  style={{ fontSize: '0.78rem' }}
                                >
                                  <MenuItem value="low">Low</MenuItem>
                                  <MenuItem value="high">High</MenuItem>
                                </Select>
                              </div>
                              {/* Weed Pressure */}
                              <div className={classes.modifierRow}>
                                <span className={classes.modifierLabel}>Weed Pressure</span>
                                <Select
                                  size="small"
                                  value={selectedArea.modifiers?.weedPressure || 'low'}
                                  onChange={(e) => handleUpdateArea(selectedAreaId, 'modifiers', { weedPressure: e.target.value })}
                                  style={{ fontSize: '0.78rem' }}
                                >
                                  <MenuItem value="low">Low</MenuItem>
                                  <MenuItem value="med">Med</MenuItem>
                                  <MenuItem value="high">High</MenuItem>
                                </Select>
                              </div>
                              {/* Limited Access */}
                              <div className={classes.modifierRow}>
                                <span className={classes.modifierLabel}>Limited Access</span>
                                <Checkbox
                                  size="small"
                                  checked={selectedArea.modifiers?.limitedAccess || false}
                                  onChange={(e) => handleUpdateArea(selectedAreaId, 'modifiers', { limitedAccess: e.target.checked })}
                                />
                              </div>
                              {/* Blow Restrictions */}
                              <div className={classes.modifierRow}>
                                <span className={classes.modifierLabel}>Blow Restrictions</span>
                                <Checkbox
                                  size="small"
                                  checked={selectedArea.modifiers?.blowRestrictions || false}
                                  onChange={(e) => handleUpdateArea(selectedAreaId, 'modifiers', { blowRestrictions: e.target.checked })}
                                />
                              </div>
                              {/* Irrigation */}
                              <div className={classes.modifierRow}>
                                <span className={classes.modifierLabel}>Irrigation</span>
                                <Select
                                  size="small"
                                  value={selectedArea.modifiers?.irrigationIncluded || 'none'}
                                  onChange={(e) => handleUpdateArea(selectedAreaId, 'modifiers', { irrigationIncluded: e.target.value })}
                                  style={{ fontSize: '0.78rem' }}
                                >
                                  <MenuItem value="none">None</MenuItem>
                                  <MenuItem value="basic">Basic</MenuItem>
                                  <MenuItem value="full">Full</MenuItem>
                                </Select>
                              </div>
                            </Box>
                          </Collapse>
                        </Box>
                      </Box>
                    )}

                    {/* ===== SERVICES TAB ===== */}
                    {editorTab === 1 && (
                      <Box pt={1}>
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 6 }}>
                          Click to toggle services for this area. Defaults are auto-suggested based on area type.
                        </Typography>

                        <div className={classes.servicesWrap}>
                          {Object.entries(SERVICES).map(([key, svcValue]) => {
                            const isActive = (selectedArea.servicesRequired || []).includes(svcValue);
                            const isSuggested = getSuggestedServices(selectedArea.areaType).includes(svcValue);
                            return (
                              <Chip
                                key={svcValue}
                                label={SERVICE_LABELS[svcValue]}
                                size="small"
                                className={isActive ? classes.serviceChipActive : classes.serviceChip}
                                variant={isActive ? 'default' : 'outlined'}
                                onClick={() => {
                                  const current = selectedArea.servicesRequired || [];
                                  const next = isActive
                                    ? current.filter(s => s !== svcValue)
                                    : [...current, svcValue];
                                  handleUpdateArea(selectedAreaId, 'servicesRequired', next);
                                }}
                              />
                            );
                          })}
                        </div>

                        {/* Quick actions */}
                        <Box mt={1} display="flex" style={{ gap: 8 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            style={{ fontSize: '0.7rem', textTransform: 'none' }}
                            onClick={() => {
                              const suggested = getSuggestedServices(selectedArea.areaType);
                              handleUpdateArea(selectedAreaId, 'servicesRequired', suggested);
                            }}
                          >
                            Reset to Defaults
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            style={{ fontSize: '0.7rem', textTransform: 'none' }}
                            onClick={() => handleUpdateArea(selectedAreaId, 'servicesRequired', [])}
                          >
                            Clear All
                          </Button>
                        </Box>
                      </Box>
                    )}

                  </div>
                  );
                })()}
              </>
            )}

            {activeTab === 1 && (
              <>
                <SiteTakeoffSummary
                  areas={areas}
                  onSelectArea={(a) => {
                    setSelectedAreaId(a._id || a.id);
                    setActiveTab(0);
                  }}
                />
                <LaborEstimate areas={areas} rateOverrides={rateOverrides} />
                <Divider style={{ margin: '12px 0' }} />
                <ProductionRateEditor
                  areas={areas}
                  rateOverrides={rateOverrides}
                  onChange={setRateOverrides}
                />
              </>
            )}
          </div>

          {/* Save Bar */}
          <div className={classes.saveBar}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving || !jobSite?._id || areas.length === 0}
              fullWidth
              size="small"
            >
              {saving ? 'Saving...' : 'Save All Areas'}
            </Button>
          </div>
        </Paper>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.type}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  notification: state.notification,
  clients: state.clients,
});

export default connect(mapStateToProps)(React.memo(SiteTakeoffPage));
